/**
 * Optimistic Updates System
 * 
 * Provides instant UI feedback with automatic rollback on failure.
 * Handles conflict resolution and maintains data consistency.
 */

import { 
  createAction,
  nanoid,
  AnyAction,
  Middleware,
} from '@reduxjs/toolkit';
import { reduxLogger } from '@/core/utils/logger';
// Local type definitions (instead of importing from internal RTK Query APIs)
type PatchCollection = (draft: unknown) => void;
import type { RootState } from '../../core/store/rootReducer';

// Optimistic update types
export interface OptimisticUpdate {
  id: string;
  timestamp: number;
  action: AnyAction;
  rollbackAction: AnyAction;
  endpoint: string;
  status: 'pending' | 'confirmed' | 'failed';
}

// Optimistic actions
export const optimisticActions = {
  apply: createAction<OptimisticUpdate>('optimistic/apply'),
  confirm: createAction<{ id: string; serverData?: unknown }>('optimistic/confirm'),
  rollback: createAction<{ id: string; reason?: string }>('optimistic/rollback'),
  clear: createAction('optimistic/clear'),
};

// Optimistic update manager
export class OptimisticUpdateManager {
  private updates: Map<string, OptimisticUpdate> = new Map();
  private rollbackQueue: Set<string> = new Set();
  
  /**
   * Create optimistic update
   */
  createUpdate(
    endpoint: string,
    action: AnyAction,
    rollbackAction: AnyAction
  ): OptimisticUpdate {
    const update: OptimisticUpdate = {
      id: nanoid(),
      timestamp: Date.now(),
      action,
      rollbackAction,
      endpoint,
      status: 'pending',
    };
    
    this.updates.set(update.id, update);
    return update;
  }
  
  /**
   * Confirm optimistic update
   */
  confirm(id: string, serverData?: unknown): void {
    const update = this.updates.get(id);
    if (update) {
      update.status = 'confirmed';
      
      // Reconcile with server data if provided
      if (serverData) {
        reduxLogger.info(`[Optimistic] Reconciling update ${id} with server data`);
      }
    }
  }
  
  /**
   * Rollback optimistic update
   */
  rollback(id: string, reason?: string): AnyAction | null {
    const update = this.updates.get(id);
    if (update && update.status === 'pending') {
      reduxLogger.info(`[Optimistic] Rolling back update ${id}: ${reason}`);
      update.status = 'failed';
      this.rollbackQueue.add(id);
      return update.rollbackAction;
    }
    return null;
  }
  
  /**
   * Get pending updates for endpoint
   */
  getPendingForEndpoint(endpoint: string): OptimisticUpdate[] {
    return Array.from(this.updates.values())
      .filter(u => u.endpoint === endpoint && u.status === 'pending');
  }
  
  /**
   * Clean old updates
   */
  cleanup(maxAge: number = 5 * 60 * 1000): void {
    const cutoff = Date.now() - maxAge;
    for (const [id, update] of this.updates) {
      if (update.timestamp < cutoff && update.status !== 'pending') {
        this.updates.delete(id);
      }
    }
  }
}

// Global instance
const optimisticManager = new OptimisticUpdateManager();

/**
 * Optimistic update utilities for RTK Query
 */
export const optimisticUtils = {
  /**
   * Create optimistic mutation
   */
  createOptimisticMutation: <T extends { id?: string }>(
    data: T,
    options?: {
      generateId?: boolean;
      timestamp?: boolean;
    }
  ): T & { _optimistic: true } => {
    const optimisticData = { ...data } as T & { _optimistic: true };
    
    if (options?.generateId && !optimisticData.id) {
      (optimisticData as T & { id: string; _optimistic: true }).id = `temp_${nanoid()}`;
    }
    
    if (options?.timestamp) {
      (optimisticData as T & { createdAt: string; _optimistic: true }).createdAt = new Date().toISOString();
      (optimisticData as T & { updatedAt: string; _optimistic: true }).updatedAt = new Date().toISOString();
    }
    
    optimisticData._optimistic = true;
    
    return optimisticData;
  },
  
  /**
   * Create rollback data snapshot
   */
  createRollbackSnapshot: (state: unknown, path: string[]): unknown => {
    let current = state;
    for (const key of path) {
      current = (current as Record<string, unknown>)?.[key];
    }
    return JSON.parse(JSON.stringify(current));
  },
  
  /**
   * Apply optimistic update to cache
   */
  applyOptimisticUpdate: (
    draft: Record<string, unknown>,
    update: Record<string, unknown>,
    options?: {
      mergeStrategy?: 'replace' | 'merge' | 'prepend' | 'append';
      idField?: string;
    }
  ) => {
    const { mergeStrategy = 'merge', idField = 'id' } = options || {};
    
    switch (mergeStrategy) {
      case 'replace':
        return update;
        
      case 'prepend':
        if (Array.isArray(draft)) {
          draft.unshift(update);
        }
        break;
        
      case 'append':
        if (Array.isArray(draft)) {
          draft.push(update);
        }
        break;
        
      case 'merge':
      default:
        if (Array.isArray(draft) && update[idField]) {
          const index = draft.findIndex((item: Record<string, unknown>) => item[idField] === update[idField]);
          if (index >= 0) {
            draft[index] = { ...draft[index], ...update };
          } else {
            draft.push(update);
          }
        } else if (typeof draft === 'object') {
          Object.assign(draft, update);
        }
    }
  },
  
  /**
   * Remove optimistic item from cache
   */
  removeOptimisticItem: (
    draft: Record<string, unknown>[],
    id: string,
    idField: string = 'id'
  ) => {
    const index = draft.findIndex(item => item[idField] === id);
    if (index >= 0) {
      draft.splice(index, 1);
    }
  },
  
  /**
   * Replace temporary ID with server ID
   */
  replaceOptimisticId: (
    draft: Record<string, unknown>,
    tempId: string,
    realId: string,
    idField: string = 'id'
  ) => {
    if (Array.isArray(draft)) {
      const item = draft.find(i => i[idField] === tempId);
      if (item) {
        item[idField] = realId;
        delete item._optimistic;
      }
    }
  },
};

/**
 * Optimistic update middleware
 */
export const optimisticMiddleware: Middleware<object, RootState> = 
  (store) => (next) => (action: unknown) => {
    // Handle optimistic actions
    if (optimisticActions.apply.match(action)) {
      const update = action.payload;
      reduxLogger.info(`[OptimisticMiddleware] Applying optimistic update: ${update.id}`);
      
      // Apply the optimistic action
      next(update.action);
      
      // Track the update
      return next(action);
    }
    
    if (optimisticActions.rollback.match(action)) {
      const { id, reason } = action.payload;
      const rollbackAction = optimisticManager.rollback(id, reason);
      
      if (rollbackAction) {
        // Apply rollback
        next(rollbackAction);
      }
      
      return next(action);
    }
    
    // Handle RTK Query mutations
    const typedAction = action as { 
      type?: string; 
      meta?: { 
        endpoint?: string; 
        arg?: { optimistic?: unknown; rollback?: unknown }; 
        optimisticId?: string;
      };
      payload?: unknown;
    };
    if (typedAction.type?.includes('/pending') && typedAction.meta?.arg?.optimistic) {
      const { endpoint, arg } = typedAction.meta;
      const { optimistic, rollback } = arg || {};
      
      // Create optimistic update
      const update = optimisticManager.createUpdate(
        endpoint || '',
        { type: 'optimistic/data', payload: optimistic },
        { type: 'optimistic/rollback', payload: rollback }
      );
      
      // Apply optimistic update
      store.dispatch(optimisticActions.apply(update));
      
      // Add update ID to action meta
      if (typedAction.meta) {
        (typedAction.meta as { optimisticId?: string }).optimisticId = update.id;
      }
    }
    
    // Handle success
    if (typedAction.type?.includes('/fulfilled') && typedAction.meta?.optimisticId) {
      store.dispatch(
        optimisticActions.confirm({
          id: typedAction.meta.optimisticId,
          serverData: typedAction.payload,
        })
      );
    }
    
    // Handle failure
    if (typedAction.type?.includes('/rejected') && typedAction.meta?.optimisticId) {
      store.dispatch(
        optimisticActions.rollback({
          id: typedAction.meta.optimisticId,
          reason: (typedAction as { error?: { message?: string } }).error?.message,
        })
      );
    }
    
    return next(action);
  };

/**
 * RTK Query optimistic update helpers
 */
export const rtkOptimisticHelpers = {
  /**
   * Optimistic add to list
   */
  optimisticAddToList: (
    listKey: string,
    item: Record<string, unknown>,
    options?: {
      position?: 'start' | 'end';
      sortFn?: (a: Record<string, unknown>, b: Record<string, unknown>) => number;
    }
  ): PatchCollection => {
    return (draft: unknown) => {
      const typedDraft = draft as Record<string, unknown>;
      const list = (typedDraft[listKey] as unknown[]) || [];
      const optimisticItem = optimisticUtils.createOptimisticMutation(item, {
        generateId: true,
        timestamp: true,
      });
      
      if (options?.position === 'start') {
        list.unshift(optimisticItem);
      } else {
        list.push(optimisticItem);
      }
      
      if (options?.sortFn) {
        list.sort(options.sortFn as (a: unknown, b: unknown) => number);
      }
      
      typedDraft[listKey] = list;
    };
  },
  
  /**
   * Optimistic update item
   */
  optimisticUpdateItem: (
    listKey: string,
    id: string,
    updates: Record<string, unknown>
  ): PatchCollection => {
    return (draft: unknown) => {
      const typedDraft = draft as Record<string, unknown>;
      const list = (typedDraft[listKey] as Record<string, unknown>[]) || [];
      const index = list.findIndex((item: Record<string, unknown>) => item.id === id);
      
      if (index >= 0) {
        list[index] = {
          ...list[index],
          ...updates,
          _optimistic: true,
          updatedAt: new Date().toISOString(),
        };
      }
      typedDraft[listKey] = list;
    };
  },
  
  /**
   * Optimistic remove from list
   */
  optimisticRemoveFromList: (
    listKey: string,
    id: string
  ): PatchCollection => {
    return (draft: unknown) => {
      const typedDraft = draft as Record<string, unknown>;
      const list = (typedDraft[listKey] as Record<string, unknown>[]) || [];
      typedDraft[listKey] = list.filter((item: Record<string, unknown>) => item.id !== id);
    };
  },
};

/**
 * React hook for optimistic updates
 */
export function useOptimisticUpdate<T>(
  initialData: T
) {
  const [optimisticData, setOptimisticData] = React.useState(initialData);
  const [isOptimistic, setIsOptimistic] = React.useState(false);
  const updateIdRef = React.useRef<string | null>(null);
  
  // Store options for future use in rollback/conflict resolution (removed unused ref)
  
  const applyOptimistic = React.useCallback((update: Partial<T>) => {
    setOptimisticData(prev => ({ ...prev, ...update }));
    setIsOptimistic(true);
    updateIdRef.current = nanoid();
    return updateIdRef.current;
  }, []);
  
  const confirmOptimistic = React.useCallback((serverData: T) => {
    setOptimisticData(serverData);
    setIsOptimistic(false);
    updateIdRef.current = null;
  }, []);
  
  const rollbackOptimistic = React.useCallback(() => {
    setOptimisticData(initialData);
    setIsOptimistic(false);
    updateIdRef.current = null;
  }, [initialData]);
  
  return {
    data: optimisticData,
    isOptimistic,
    applyOptimistic,
    confirmOptimistic,
    rollbackOptimistic,
  };
}

// Import React for the hook
import * as React from 'react';

// Cleanup old updates periodically.
// Guard against duplicate intervals during HMR re-evaluation by storing
// the timer ID on the global object and clearing any previous interval
// before creating a new one.
if (typeof window !== 'undefined') {
  const HMR_KEY = '__optimisticCleanupInterval';
  const globalWindow = window as unknown as Record<string, ReturnType<typeof setInterval> | undefined>;
  if (globalWindow[HMR_KEY]) {
    clearInterval(globalWindow[HMR_KEY]);
  }
  globalWindow[HMR_KEY] = setInterval(() => {
    optimisticManager.cleanup();
  }, 60000); // Every minute
}
